import React from 'react';
import './HowToExportCsv.css';
import PageHeader from '../components/PageHeader';
import { useBrandIdentity } from '../utils/brandIdentity';

function HowToExportCsv({ onNavigate }) {
  const { appName } = useBrandIdentity();
  return (
    <>
      <PageHeader onNavigate={onNavigate} />
      <div className="how-to-export-csv-page">
      <div className="page-content">
        <h1>How to Export CSV Files from NinjaTrader</h1>
        <p className="page-intro">
          Follow these steps to export your account and strategy data from NinjaTrader and upload them to {appName} for bulk risk analysis.
        </p>

        {/* YouTube Tutorial Video */}
        <section className="instruction-section">
          <div className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%', background: '#000', borderRadius: '12px', marginBottom: '2rem' }}>
            <iframe
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              src="https://www.youtube.com/embed/OvZUKQI12_E"
              title={`How to Export NinjaTrader CSVs to ${appName}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>

        <section className="instruction-section">
          <h2>Step 1: Export Accounts CSV</h2>
          <div className="step-content">
            <ol className="instruction-steps">
              <li>
                <strong>Open NinjaTrader</strong>
                <p>Launch your NinjaTrader platform and ensure you're connected to your accounts.</p>
              </li>
              <li>
                <strong>Navigate to Accounts Tab</strong>
                <p>In NinjaTrader, go to the <strong>Accounts</strong> tab or window where you can see all your connected accounts.</p>
              </li>
              <li>
                <strong>Export Accounts Data</strong>
                <p>
                  Right-click on the accounts list or look for an <strong>"Export"</strong> or <strong>"Export to CSV"</strong> option in the Accounts window menu.
                  <br />
                  <em>Note: The exact location may vary by NinjaTrader version. Look for File → Export or a right-click context menu.</em>
                </p>
              </li>
              <li>
                <strong>Configure Required Columns</strong>
                <p>
                  Before exporting, right-click in the Accounts grid and select <strong>Properties</strong>. Make sure these three columns are checked:
                  <br />
                  • <strong>Display name</strong> - Account identifier
                  <br />
                  • <strong>Net liquidation</strong> - Current balance
                  <br />
                  • <strong>Trailing max drawdown</strong> - Current drawdown
                  <br />
                  Click OK to save.
                </p>
              </li>
              <li>
                <strong>Save the File</strong>
                <p>
                  Right-click in the Accounts grid and select <strong>Export</strong>. Save the file with a unique name like <code>ac3.csv</code> or <code>accounts-jan20.csv</code>
                </p>
              </li>
            </ol>
          </div>
        </section>

        <section className="instruction-section">
          <h2>Step 2: Export Strategies CSV</h2>
          <div className="step-content">
            <ol className="instruction-steps">
              <li>
                <strong>Open Strategies Window</strong>
                <p>In NinjaTrader, navigate to the <strong>Strategies</strong> tab or window where you can see all your running strategies.</p>
              </li>
              <li>
                <strong>Configure Required Columns</strong>
                <p>
                  Before exporting, right-click in the Strategy Performance grid and select <strong>Properties</strong>. Make sure these three columns are checked:
                  <br />
                  • <strong>Account Display Name</strong> - Links strategy to account
                  <br />
                  • <strong>Instrument</strong> - Contract type (NQ/MNQ)
                  <br />
                  • <strong>Parameters</strong> - Contains contract count (critical!)
                  <br />
                  Click OK to save.
                </p>
              </li>
              <li>
                <strong>Save the File</strong>
                <p>
                  Right-click in the Strategy Performance grid and select <strong>Export</strong>. Save the file with a unique name like <code>st3.csv</code> or <code>strategies-jan20.csv</code>
                </p>
              </li>
            </ol>
          </div>
        </section>

        <section className="instruction-section">
          <h2>Step 3: Upload to {appName}</h2>
          <div className="step-content">
            <ol className="instruction-steps">
              <li>
                <strong>Navigate to CSV Upload Section</strong>
                <p>
                  On the {appName} dashboard, scroll down to the <strong>"NinjaTrader CSV Upload"</strong> section
                  <br />
                  <em>Note: This feature is available in {appName} Pro. Upgrade if you haven't already.</em>
                </p>
              </li>
              <li>
                <strong>Upload Accounts CSV</strong>
                <p>
                  Click the <strong>"Browse Accounts"</strong> button and select your exported Accounts CSV file.
                  <br />
                  You'll see the filename appear once selected.
                </p>
              </li>
              <li>
                <strong>Upload Strategies CSV</strong>
                <p>
                  Click the <strong>"Browse Strategies"</strong> button and select your exported Strategies CSV file.
                  <br />
                  Both files should now be selected and ready to process.
                </p>
              </li>
              <li>
                <strong>Alternative: Auto-Detect</strong>
                <p>
                  If you're not sure which file is which, you can use the <strong>"Auto-Detect"</strong> option.
                  <br />
                  Upload one file at a time, and {appName} will automatically identify whether it's an Accounts or Strategies file.
                </p>
              </li>
              <li>
                <strong>Parse & Load</strong>
                <p>
                  Click the <strong>"Parse & Load into Bulk Calculator"</strong> button.
                  <br />
                  {appName} will:
                  <ul className="feature-list">
                    <li>Parse both CSV files</li>
                    <li>Match accounts to their assigned strategies</li>
                    <li>Extract account sizes, balances, drawdowns, and contract counts</li>
                    <li>Auto-populate the Bulk Risk Calculator with all matched combinations</li>
                  </ul>
                </p>
              </li>
              <li>
                <strong>Review & Analyze</strong>
                <p>
                  Once loaded, scroll to the <strong>"Bulk Risk Assessment"</strong> section above.
                  <br />
                  You'll see all your accounts populated with their data. Review the information, make any adjustments if needed, then click <strong>"Analyze"</strong> to run risk calculations for all accounts at once.
                </p>
              </li>
            </ol>
          </div>
        </section>

        <section className="instruction-section">
          <h2>CSV File Format Requirements</h2>
          <div className="step-content">
            <div className="csv-requirements">
              <div className="requirement-box">
                <h3>Accounts CSV Must Include:</h3>
                <ul>
                  <li><strong>Display name</strong> - The account identifier (e.g., PAAPEX3982600000002)</li>
                  <li><strong>Net liquidation</strong> - Current account balance</li>
                  <li><strong>Trailing max drawdown</strong> - Current trailing drawdown value</li>
                </ul>
              </div>
              <div className="requirement-box">
                <h3>Strategies CSV Must Include:</h3>
                <ul>
                  <li><strong>Account Display Name</strong> - Links strategy to account</li>
                  <li><strong>Instrument</strong> - Contract type (NQ or MNQ)</li>
                  <li><strong>Parameters</strong> - Used to extract contract count (critical!)</li>
                </ul>
                <p style={{ marginTop: '0.75rem', fontSize: '0.9em', color: 'rgba(255,255,255,0.7)' }}>
                  <em>Note: Configure these columns via right-click → Properties in Strategy Performance tab</em>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="instruction-section">
          <h2>Tips & Troubleshooting</h2>
          <div className="step-content">
            <div className="tips-box">
              <h3>✅ Tips for Success:</h3>
              <ul>
                <li>Make sure strategy names in your Strategies CSV match the strategy names shown in {appName} exactly (or closely)</li>
                <li>Export both files from the same NinjaTrader session to ensure data consistency</li>
                <li>Account sizes are automatically determined from balances (rounded to nearest preset: 25K, 50K, 100K, etc.)</li>
                <li>Contract counts are extracted from strategy parameters - verify these are correct</li>
              </ul>
            </div>
            <div className="tips-box">
              <h3>⚠️ Common Issues:</h3>
              <ul>
                <li><strong>"No matching accounts and strategies found"</strong> - Check that strategy names match the names shown in {appName}</li>
                <li><strong>"CSV file missing required columns"</strong> - Ensure your export includes all required columns listed above</li>
                <li><strong>"No valid data found"</strong> - Check that your CSV files aren't empty and contain actual account/strategy data</li>
                <li><strong>Account names not showing</strong> - The account name is automatically extracted from the "Display name" column</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="instruction-section">
          <div className="back-button-container">
            <button 
              className="back-to-dashboard-btn"
              onClick={() => onNavigate && onNavigate('home')}
            >
              ← Back to Dashboard
            </button>
          </div>
        </section>
      </div>
    </div>
    </>
  );
}

export default HowToExportCsv;

